import React, {MouseEvent, ReactNode, useCallback} from 'react';

import {toast} from 'react-toastify';

import {useDispatch} from 'react-redux';

import {useNavigate} from 'react-router-dom';

import {t} from '@lingui/core/macro';

import {IUserOutput, userAPI, fetchMe, impersonate} from 'entities/user';

import MoreActionsItem from '../../../../shared/ui/MoreActions/MoreActionsItem';
import MoreActionsDots from '../../../../shared/ui/MoreActions/MoreActionsDots';

import {TypedDispatch} from '../../../../shared/model/types';
import {redirectToDashboard} from '../../../../shared/api/redirects';
import {clearCachedData} from '../../../../shared/api/auth';
import EmailActive from '../../../../shared/ui/icons/TablesMenu/EmailActive';

import ConfirmEmail from '../../../../shared/ui/icons/TablesMenu/ConfirmEmail';
import Edit from '../../../../shared/ui/icons/General/Edit';
import InstallTariffs from '../../../../shared/ui/icons/TablesMenu/InstallTariffs';
import LogInAs from '../../../../shared/ui/icons/TablesMenu/LogInAs';
import Unblock from '../../../../shared/ui/icons/General/Unblock';
import Block from '../../../../shared/ui/icons/TablesMenu/Block';
import Delete from '../../../../shared/ui/icons/General/Delete';

interface IUserMenuProps {
    item: IUserOutput,
    // eslint-disable-next-line no-unused-vars
    onEditClick: (event: MouseEvent<HTMLTableRowElement>) => void,
    // eslint-disable-next-line no-unused-vars
    onTariffClick: (event: MouseEvent<HTMLTableRowElement>) => void,
}

const UserMenu: React.FC<IUserMenuProps> = ({item, onEditClick, onTariffClick}) => {
    const dispatch = useDispatch<TypedDispatch>();
    const navigate = useNavigate();
    const [confirmUserEmail] = userAPI.useConfirmUserEmailMutation();
    const [changeBlock] = userAPI.useChangeUserBlockMutation();
    const [fixEmailStatus] = userAPI.useFixEmailStatusMutation();
    const [deleteUser] = userAPI.useDeleteUserMutation();

    const onClickActivate = useCallback(async(event: MouseEvent) => {
        event.stopPropagation();
        try {
            await confirmUserEmail({
                id: item.id,
                email_confirmed: true,
            }).unwrap();
            toast.info(t`Email подтвержден`);
        }catch (e){

        }

    }, [confirmUserEmail, item.id]);

    const onImpersonateClick = useCallback(async(event: MouseEvent) => {
        event.stopPropagation();

        try {
            const returnUrl = window.location.pathname + window.location.search;
            localStorage.setItem('return_url_after_impersonation', returnUrl);

            await dispatch(impersonate(item.id)).unwrap();
            toast.info(t`Вы вошли как пользователь ` + item.email);
            await dispatch(fetchMe()).unwrap();

            redirectToDashboard(navigate);

            await clearCachedData(dispatch);
        } catch (e) {}
    }, [dispatch, item.email, item.id, navigate]);

    const handleFixEmail = async(userId: number) => {
        try {
            await fixEmailStatus({ id: userId }).unwrap();
            toast.info(t`Статус почты успешно исправлен`);
        } catch (error) {
        }
    };

    const onToggleBlock = useCallback(async(event: MouseEvent) => {
        event.stopPropagation();

        try {
            await changeBlock({
                id: item.id,
                blocked: !item.blocked,
            }).unwrap();
            toast.info(t`Пользователь `+(item.blocked ? t`разблокирован` : t`заблокирован`));
        } catch (e) {

        }
    }, [changeBlock, item.blocked, item.id]);

    const handleDeleteUser = useCallback(async(userId: number) => {
        try {
            await deleteUser(userId).unwrap();
            toast.info(t`Пользователь успешно удален`);
        } catch (error) {
        }
    }, [deleteUser]);

    const getActions = (): ReactNode[] => {
        const actions: ReactNode[] = [];
        if (!item.email_confirmed) {
            actions.push(
                <MoreActionsItem
                    key="off"
                    label="Подтвердить Email"
                    icon=<ConfirmEmail/>
                    onClick={onClickActivate}
                />,
            );
        }

        actions.push(
            <MoreActionsItem
                key="edit"
                label="Редактировать"
                icon=<Edit/>
                onClick={onEditClick}/>,
        );

        actions.push(
            <MoreActionsItem
                key="tariff"
                label="Установить тариф"
                icon=<InstallTariffs/>
                onClick={onTariffClick}/>,
        );

        actions.push(
            <MoreActionsItem
                key="impersonate"
                label="Войти как"
                icon=<LogInAs/>
                onClick={onImpersonateClick}/>,
        );

        actions.push(
            <MoreActionsItem
                key="blocked"
                label={item.blocked ? 'Разблокировать' : 'Заблокировать'}
                icon={item.blocked ? <Block/> : <Unblock/>}
                onClick={onToggleBlock}/>,
        );

        if (item.last_email_error !== null) {
            actions.push(
                <MoreActionsItem
                    key="fix-email"
                    label="Почта исправна, активировать"
                    icon=<EmailActive/>
                    onClick={async(event) => {
                        event.stopPropagation();
                        await handleFixEmail(item.id);
                    }}
                />,
            );
        }

        actions.push(
            <MoreActionsItem
                key="delete"
                label="Удалить"
                icon=<Delete/>
                onClick={async(event) => {
                    event.stopPropagation();
                    await handleDeleteUser(item.id);
                }}
            />,
        );

        return actions;
    };

    return (
        <MoreActionsDots placement="auto">
            {
                getActions().map(el => el)
            }
        </MoreActionsDots>
    );
};

export default React.memo(UserMenu);